import { useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { getAttendees, getCalendarEvents, updatePlannerDate } from "./CalendarService";
import { createItem } from "./planner/PlannerService";
import "./CalendarPage.css";

const TYPE_OPTIONS = ["TODO", "IMPORTANT_DATE", "PAYMENT", "REMINDER"];
const RECURRENCE_OPTIONS = ["NONE", "DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
const CATEGORY_OPTIONS = [
  "WORK", "PERSONAL", "FINANCE", "HEALTH", "FAMILY", "EDUCATION",
  "BUSINESS", "SHOPPING", "TRAVEL", "BILLING", "INVESTMENT",
  "SUBSCRIPTION", "INSURANCE", "HOME", "SOCIAL", "GOALS",
  "OTHER", "PROJECT",
];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [form, setForm] = useState({
    title: "",
    type: "TODO",
    category: "WORK",
    date: "",
    allDay: true,
    startTime: "",
    endTime: "",
    recurrence: "NONE",
    recurrenceEndDate: "",
    attendeeUserIds: [],
  });
  const [attendees, setAttendees] = useState([]);
  const [attendeesError, setAttendeesError] = useState("");


  const fetchEvents = (startStr, endStr) => {
    setError("");
    getCalendarEvents(startStr, endStr)
      .then((data) => {
        const mapped = data.map((e) => ({
          id: e.id,
          title: e.title,
          start: e.start,
          end: e.end || undefined,
          allDay: e.allDay,
          editable: e.editable,
          eventStartEditable: e.editable,
          eventDurationEditable: e.editable,
          extendedProps: {
            type: e.type,
            category: e.category,
            recurrence: e.recurrence,
            baseId: e.baseId,
          },
          ...eventColors(e.type, e.category),
        }));
        setEvents(mapped);
      })
      .catch(() => setError("Unable to load calendar data."));
  };

  const isOwner = () => {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    return auth?.userId === "usr-c25932f5-3093-4ee0-9076-9cd1055fcd55";
  };

  const loadAttendees = () => {
    if (!isOwner()) return;
    getAttendees()
      .then((data) => {
        setAttendees(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAttendeesError("Unable to load people list.");
      });
  };

  const handleEventDrop = async (info) => {
    if (info.event.extendedProps.recurrence && info.event.extendedProps.recurrence !== "NONE") {
      info.revert();
      setError("Recurring events cannot be moved.");
      return;
    }
    const id = info.event.id;
    const date = info.event.startStr.slice(0, 10);
    const allDay = info.event.allDay;
    const startTime = allDay ? null : formatTime(info.event.start);
    const endTime = allDay ? null : formatTime(info.event.end);
    try {
      await updatePlannerDate(id, date, startTime, endTime, allDay);
    } catch {
      info.revert();
      setError("Unable to update date.");
    }
  };

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setForm((prev) => ({ ...prev, date: info.dateStr }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload = buildPlannerPayload(form);
    try {
      const created = await createItem(payload);
      const createdDate = calendarDateFromItem(created);
      if (createdDate) {
        setEvents((prev) => [
          ...prev,
          {
            id: created.id,
            title: created.title,
            start: calendarStartFromItem(created),
            end: calendarEndFromItem(created),
            allDay: isAllDay(created),
            editable: !created.recurrence || created.recurrence === "NONE",
            extendedProps: {
              type: created.type,
              category: created.category,
              recurrence: created.recurrence,
              baseId: created.id,
            },
            ...eventColors(created.type, created.category),
          },
        ]);
      }
      setForm((prev) => ({ ...prev, title: "" }));
    } catch {
      setError("Unable to create item.");
    }
  };

  const formDateValue = form.date || selectedDate;


  return (
    <div className="calendar-page">
      <div className="calendar-main">
        <div className="calendar-toolbar">
          <h2 className="calendar-title">Calendar</h2>
          <form className="calendar-form-inline" onSubmit={handleCreate}>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-title">Title</label>
              <input
                id="calendar-title"
                className="calendar-input"
                placeholder="Add event"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-type">Type</label>
              <select
                id="calendar-type"
                className="calendar-select"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              >
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-category">Category</label>
              <select
                id="calendar-category"
                className="calendar-select"
                value={form.category}
                onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
              >
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {isOwner() && (
              <div className="calendar-field calendar-field-span">
                <label className="calendar-label">Invite people</label>
                <div className="calendar-attendees">
                  {attendees.length === 0 && !attendeesError && (
                    <button
                      type="button"
                      className="calendar-btn calendar-btn-secondary"
                      onClick={loadAttendees}
                    >
                      Load people
                    </button>
                  )}
                  {attendeesError && <div className="calendar-muted">{attendeesError}</div>}
                  {attendees.map((person) => (
                    <label key={person.id} className="calendar-attendee-item">
                      <input
                        type="checkbox"
                        checked={form.attendeeUserIds.includes(person.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            attendeeUserIds: checked
                              ? [...prev.attendeeUserIds, person.id]
                              : prev.attendeeUserIds.filter((id) => id !== person.id),
                          }));
                        }}
                      />
                      <span>{person.firstName} {person.lastName}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-date">Date</label>
              <input
                id="calendar-date"
                className="calendar-input"
                type="date"
                value={formDateValue}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="calendar-field calendar-field-inline">
              <label className="calendar-label" htmlFor="calendar-allday">All day</label>
              <label className="calendar-inline-toggle">
                <input
                  id="calendar-allday"
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      allDay: checked,
                      startTime: checked ? "" : prev.startTime,
                      endTime: checked ? "" : prev.endTime,
                    }));
                  }}
                />
                <span>Enabled</span>
              </label>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-start">Start time</label>
              <input
                id="calendar-start"
                className="calendar-input"
                type="time"
                value={form.startTime}
                onFocus={() =>
                  setForm((prev) => ({
                    ...prev,
                    allDay: false,
                  }))
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    startTime: e.target.value,
                    allDay: e.target.value ? false : prev.allDay,
                  }))
                }
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-end">End time</label>
              <input
                id="calendar-end"
                className="calendar-input"
                type="time"
                value={form.endTime}
                onFocus={() =>
                  setForm((prev) => ({
                    ...prev,
                    allDay: false,
                  }))
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    endTime: e.target.value,
                    allDay: e.target.value ? false : prev.allDay,
                  }))
                }
              />
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-recurrence">Recurrence</label>
              <select
                id="calendar-recurrence"
                className="calendar-select"
                value={form.recurrence}
                onChange={(e) => setForm((prev) => ({ ...prev, recurrence: e.target.value }))}
              >
                {RECURRENCE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="calendar-field">
              <label className="calendar-label" htmlFor="calendar-repeat">Repeat until</label>
              <input
                id="calendar-repeat"
                className="calendar-input"
                type="date"
                disabled={form.recurrence === "NONE"}
                value={form.recurrenceEndDate}
                onChange={(e) => setForm((prev) => ({ ...prev, recurrenceEndDate: e.target.value }))}
              />
            </div>
            <div className="calendar-field calendar-field-action">
              <label className="calendar-label calendar-label-empty">Action</label>
              <button className="calendar-btn" type="submit">Create</button>
            </div>
          </form>
        </div>

        {error && <div className="calendar-muted">{error}</div>}
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,dayGridMonth,listWeek",
          }}
          height="auto"
          eventStartEditable
          eventDurationEditable
          editable
          selectable
          events={events}
          datesSet={(info) => fetchEvents(info.startStr.slice(0, 10), info.endStr.slice(0, 10))}
          eventDrop={handleEventDrop}
          dateClick={handleDateClick}
        />
      </div>
    </div>
  );
}

function buildPlannerPayload(form) {
  const hasTime = Boolean(form.startTime || form.endTime);
  const isAllDay = form.allDay && !hasTime;
  const normalizedStart = normalizeTime(form.startTime);
  const normalizedEnd = normalizeTime(form.endTime);
  const base = {
    title: form.title,
    type: form.type,
    category: form.category,
    priority: "MEDIUM",
    recurrence: form.recurrence || "NONE",
    recurrenceEndDate: form.recurrence !== "NONE" ? form.recurrenceEndDate || null : null,
    attendeeUserIds: form.attendeeUserIds || [],
  };
  const reminder =
    form.type === "REMINDER"
      ? { reminderEnabled: true, reminderBeforeDays: 0 }
      : {};
  const timeFields = isAllDay
    ? {}
    : {
        startTime: normalizedStart,
        endTime: normalizedEnd,
      };
  if (!form.date) {
    return { ...base, ...reminder, ...timeFields };
  }
  if (form.type === "IMPORTANT_DATE") {
    return { ...base, ...reminder, ...timeFields, eventDate: form.date };
  }
  if (form.type === "PAYMENT") {
    return { ...base, ...reminder, ...timeFields, paymentDate: form.date };
  }
  return { ...base, ...reminder, ...timeFields, dueDate: form.date };
}

function calendarDateFromItem(item) {
  if (item.type === "IMPORTANT_DATE") return item.eventDate;
  if (item.type === "PAYMENT") return item.paymentDate;
  return item.dueDate;
}

function isAllDay(item) {
  return !item.startTime && !item.endTime;
}

function calendarStartFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date) return null;
  if (item.startTime) {
    return `${date}T${item.startTime}`;
  }
  return date;
}

function calendarEndFromItem(item) {
  const date = calendarDateFromItem(item);
  if (!date || !item.endTime) return undefined;
  return `${date}T${item.endTime}`;
}

function formatTime(date) {
  if (!date) return null;
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

function normalizeTime(value) {
  if (!value) return null;
  return value.length === 5 ? `${value}:00` : value;
}

function eventColors(type, category) {
  const categoryPalette = {
    WORK: "#1f4acc",
    BUSINESS: "#1f4acc",
    PROJECT: "#1f4acc",
    FINANCE: "#1a6f3b",
    BILLING: "#1a6f3b",
    INVESTMENT: "#1a6f3b",
    INSURANCE: "#1a6f3b",
    SUBSCRIPTION: "#1a6f3b",
    HEALTH: "#0f766e",
    FAMILY: "#b45309",
    PERSONAL: "#2563eb",
    TRAVEL: "#7c3aed",
    SOCIAL: "#be123c",
    OTHER: "#374151",
  };
  const typePalette = {
    TODO: "#0f766e",
    REMINDER: "#b45309",
    IMPORTANT_DATE: "#b91c1c",
    PAYMENT: "#166534",
  };
  const color = categoryPalette[category] || typePalette[type] || "#374151";
  return {
    backgroundColor: color,
    borderColor: color,
    textColor: "#ffffff",
  };
}
